import { ReactNode } from "react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import { GenericForm, GenericFormProps } from "../../../Form/Forms/GenericForm";
import { FormationErrorBadge } from "@/lib/Components/Form/Components/FormationErrorBadge";
import { IconButton } from "../../../Button/IconButton";
import { FormationUnsavedChangesBadge } from "@/lib/Components/Form/Components/FormationUnsavedChangesBadge";
import { FormikValues, useFormikContext } from "formik";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { Badge } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";

export type RecordScreenFormCardProps<T> = GenericFormProps<T> & {
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  view?: ReactNode;
  actions?: ReactNode;
  isEditable?: boolean;
  adminOnly?: boolean;
  containerClassName?: string;
};

export function RecordScreenFormCard<T extends FormikValues>({
  title,
  subtitle,
  children,
  view,
  actions,
  onSubmit,
  adminOnly,
  isEditable = true,
  containerClassName,
  ...props
}: RecordScreenFormCardProps<T>) {
  const { isAdmin } = useTenant();

  if (adminOnly && !isAdmin) {
    return null;
  }

  const formContent = (
    <GenericForm<T>
      enableReinitialize
      initialEditingState={false}
      onSubmit={async (values, formikHelpers, formation) => {
        await onSubmit?.(values, formikHelpers, formation);
        formation.toggleEdit(false);
      }}
      {...props}
    >
      <FormInner
        subtitle={subtitle}
        isEditable={isEditable}
        title={title}
        view={view}
        actions={actions}
        adminOnly={adminOnly}
      >
        {children}
      </FormInner>
    </GenericForm>
  );

  if (containerClassName) {
    return <div className={containerClassName}>{formContent}</div>;
  }

  return formContent;
}

function FormInner({
  title,
  subtitle,
  children,
  view,
  actions,
  isEditable,
  adminOnly,
}: {
  title: ReactNode;
  subtitle: ReactNode;
  isEditable: boolean;
  view: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  adminOnly?: boolean;
}) {
  const { resetForm, isSubmitting } = useFormikContext();
  const { isEditing, toggleEdit } = useFormEditState();

  return (
    <>
      <header className="isolate z-20 col-span-full flex min-h-[48px] pt-3">
        <div className="grow">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            <div className="flex space-x-4">
              <span>{title}</span>
              {adminOnly ? <Badge color="blue">admin</Badge> : null}
              <FormationErrorBadge />
              <FormationUnsavedChangesBadge />
            </div>
          </h3>
          {subtitle && (
            <p className="mt-1 max-w-2xl text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
        {isEditable ? (
          <div className="flex items-center gap-1">
            {!isEditing ? (
              <>
                {actions}
                <IconButton
                  tooltip="Edit"
                  onClick={() => toggleEdit(true)}
                  Icon={PencilIcon}
                />
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  plain
                  onClick={() => {
                    toggleEdit(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  isLoading={isSubmitting}
                  type="submit"
                  LeadingIcon={CheckIcon}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </header>

      {view && !isEditing ? (
        <div className="col-span-full">{view}</div>
      ) : (
        children
      )}
    </>
  );
}
